/**
 * The process's content model — the posts every page reads.
 *
 * WHY THIS IS A SEPARATE MODULE. The content lives in `content/posts/*.md` and
 * is loaded by `../vite-content-plugin.ts`, which reads the filesystem and
 * therefore imports `node:fs` and `node:path`. The BROWSER must never reach
 * that: Vite stubs Node built-ins in a client bundle, so the first call throws
 * (`the requested module '…:node:path' does not provide an export named
 * 'basename'`).
 *
 * So the pages read the content from HERE — a plain value with no imports of
 * its own — and the loader fills it:
 *
 *   - THE BUILD (`scripts/prerender.mjs`) reads the content in Node, through the
 *     plugin's ASYNC loader (the one that runs the summariser), and installs it
 *     before rendering. That is the only path that sees the AI summaries.
 *   - A UNIT TEST / SCRIPT fills it from the plugin's synchronous loader, which
 *     is a plain function and resolves no virtual id — so a spec works whether
 *     or not a Vite config was found.
 *
 * This module is TYPES-ONLY apart from the slot itself, so importing it can
 * never drag a Node built-in into the browser.
 */

import type { Post, SiteContent } from './content-types.js'

let installed: SiteContent | null = null

/**
 * Install the site's content.
 *
 * @param content - The posts, newest first.
 */
export function setContent(content: SiteContent): void {
  installed = content
}

/**
 * The installed content, or `null` when nothing has filled the slot yet.
 *
 * A caller that gets `null` is one of two things: the browser (which never
 * loads the content — it hydrates the prerendered HTML) or a Node process that
 * should have called {@link setContent} first.
 */
export function getContent(): SiteContent | null {
  return installed
}

/** The installed posts, or an empty list before the content is installed. */
export function getPosts(): Post[] {
  return installed?.posts ?? []
}
